import { logger } from '@/utils/logger';

/**
 * Standardized date utilities for consistent date handling across the application
 */

/**
 * Formats a date for database storage
 * @param date Date object or ISO string or null/undefined 
 * @returns Formatted date string (YYYY-MM-DD) or null
 */
export const formatDateForDatabase = (date: Date | string | null | undefined): string | null => {
  if (!date) return null;
  
  try {
    // If it's already a Date object
    if (date instanceof Date) {
      // Ensure we're handling timezone correctly by using UTC methods
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // If it's a string
    if (typeof date === 'string') {
      // If it's already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      
      // Otherwise, try to convert and format
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) {
        logger.error('[DateUtils] Invalid date string:', date);
        return null;
      }
      
      // Use UTC to avoid timezone issues
      const year = dateObj.getUTCFullYear();
      const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    logger.error(`[DateUtils] Unsupported date type: ${typeof date}`);
    return null;
  } catch (error) {
    logger.error(`[DateUtils] Error formatting date:`, error);
    return null;
  }
};

/**
 * Parses a date string from the database into a Date object
 * @param dateString Date string from database
 * @returns Date object or null if invalid
 */
export const parseDatabaseDate = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;
  
  try {
    // For database dates, ensure we're setting to noon to avoid timezone issues
    const dateParts = dateString.split('-');
    if (dateParts.length !== 3) {
      logger.error('[DateUtils] Invalid date format:', dateString);
      return null;
    }
    
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10) - 1; // JS months are 0-based
    const day = parseInt(dateParts[2], 10);
    
    // Create date at noon to avoid timezone issues
    const date = new Date(Date.UTC(year, month, day, 12, 0, 0));
    
    if (isNaN(date.getTime())) {
      logger.error('[DateUtils] Invalid date components:', { year, month, day, dateString });
      return null;
    }
    
    return date;
  } catch (error) {
    logger.error(`[DateUtils] Error parsing date:`, error);
    return null;
  }
};

/**
 * Validates that a date is in the correct format for Supabase
 * @param dateString Date string to validate
 * @returns Boolean indicating if the date is valid
 */
export const isValidDatabaseDateFormat = (dateString: string | null | undefined): boolean => {
  if (!dateString) return true; // null/undefined is considered valid (means no date)
  
  // Check format is YYYY-MM-DD using regex
  const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateFormatRegex.test(dateString)) {
    return false;
  }
  
  // Verify it's a valid date (not just correct format)
  const date = parseDatabaseDate(dateString);
  return date !== null;
};

/**
 * Formats a date for display to users
 * @param dateString Date string from database or Date object
 * @returns Formatted date string for display
 */
export const formatDateForDisplay = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return "No date";
  
  try {
    const date = dateString instanceof Date ? 
      dateString : 
      parseDatabaseDate(dateString);
    
    if (!date) return "Invalid date";
    
    // Format as a user-friendly string (e.g., "Jan 15, 2023")
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    logger.error(`[DateUtils] Error formatting date for display:`, error);
    return "Invalid date";
  }
};
